<template>
  <!-- eslint-disable max-len -->
  <div class="-generating-state -app-promo">
    <!-- drifting background facets -->
    <div class="-facet -facet-a"></div>
    <div class="-facet -facet-b"></div>

    <div class="-content">
      <!-- status: appears first, stays -->
      <div class="-status">
        <span class="-status-label">GENERATING PREVIEW</span>
        <span class="-dots">
          <span class="-dot"></span>
          <span class="-dot"></span>
          <span class="-dot"></span>
        </span>
      </div>

      <!-- headline reveals in -->
      <h1 class="-headline">
        While you wait, make your next video
        <span>right from your phone.</span>
      </h1>

      <!-- subtext reveals in -->
      <p class="-subtext">
        Wochit Go turns a prompt into on-brand video. Create, edit, and
        publish<span class="-nowrap"> anywhere.</span>
      </p>

      <!-- CTA reveals in, QR pops -->
      <div class="-cta">
        <div class="-cta-row">
          <div class="-qr-card">
            <wochit-go-qr class="-qr" />
          </div>
          <div class="-cta-text">
            <div class="-scan">Scan to download</div>
            <div class="-stores">
              <a
                class="-store"
                href="https://apps.apple.com/us/app/wochit-go/id1446752890"
                target="_blank"
                rel="noopener"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"
                  />
                </svg>
                App Store
              </a>
              <a
                class="-store"
                href="https://play.google.com/store/apps/details?id=com.wochit.goapp"
                target="_blank"
                rel="noopener"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M3.6 2.2c-.2.2-.3.5-.3.9v17.8c0 .4.1.7.3.9l.1.1L14 11.9v-.2L3.7 2.1zM17.3 8.6 14.5 7 4.9 1.5c-.3-.2-.6-.2-.8-.1L15 11.8zM4.1 22.6c.2.1.5.1.8-.1L15 12.1 4.1 22.6zM20.5 10.3 18 8.9l-2.5 3 2.5 3 2.5-1.4c.7-.4.7-1.5 0-1.9z"
                  />
                </svg>
                Google Play
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WochitGoQr from 'components/WochitGoQr.vue';

export default {
  name: 'fullPreviewGeneratingState',

  components: {
    WochitGoQr,
  },
};
</script>

<style lang="scss" scoped>
// motion: smooth expo deceleration for text reveals, a gentle overshoot for
// the QR pop. Each element is a distinct, readable beat (~0.4s apart).
$ease-out-expo: cubic-bezier(0.16, 1, 0.3, 1);
$ease-back: cubic-bezier(0.34, 1.4, 0.5, 1);

@keyframes fpgPulseDot {
  0%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes fpgDriftFacet {
  0%,
  100% {
    transform: var(--rot) translate(0, 0);
  }
  50% {
    transform: var(--rot) translate(9px, -7px);
  }
}
@keyframes fpgFadeOnly {
  0% {
    opacity: 0;
    transform: translateY(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes fpgPopIn {
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  70% {
    transform: scale(1.06);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
@keyframes fpgRevealH {
  0% {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
    transform: translateY(16px);
  }
  100% {
    opacity: 1;
    max-height: 160px;
    margin-top: 18px;
    transform: translateY(0);
  }
}
@keyframes fpgRevealP {
  0% {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
    transform: translateY(16px);
  }
  100% {
    opacity: 1;
    max-height: 100px;
    margin-top: 16px;
    transform: translateY(0);
  }
}
@keyframes fpgRevealCta {
  0% {
    opacity: 0;
    max-height: 0;
    margin-top: 0;
    transform: translateY(16px);
  }
  100% {
    opacity: 1;
    max-height: 200px;
    margin-top: 26px;
    transform: translateY(0);
  }
}

// Banner fills the full-preview area (min 450px) and centers its content.
// The `.-app-promo` qualifier out-specifies the default grey
// `.wt2-full-preview .-full-preview-states` background (0,2,0).
.-generating-state.-app-promo {
  position: relative;
  display: flex;
  min-height: 450px;
  height: 100%;
  overflow: hidden;
  background: #191a4c;
  box-sizing: border-box;
}

.-facet {
  position: absolute;
  border-radius: 10px;
  pointer-events: none;
}
.-facet-a {
  top: -90px;
  left: -70px;
  width: 300px;
  height: 300px;
  background: #20265e;
  opacity: 0.5;
  --rot: rotate(20deg);
  transform: rotate(20deg);
  animation: fpgDriftFacet 13s ease-in-out infinite;
}
.-facet-b {
  bottom: -110px;
  right: -70px;
  width: 340px;
  height: 340px;
  background: #172255;
  opacity: 0.55;
  --rot: rotate(-16deg);
  transform: rotate(-16deg);
  animation: fpgDriftFacet 16s ease-in-out 1s infinite;
}

.-content {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 48px 56px;
  box-sizing: border-box;
}

.-status {
  display: flex;
  align-items: center;
  gap: 10px;
  animation: fpgFadeOnly 0.5s $ease-out-expo 0.15s both;
}
.-status-label {
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #00abdf;
}
.-dots {
  display: inline-flex;
  gap: 5px;
}
.-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #00abdf;
  display: inline-block;
  animation: fpgPulseDot 1.2s ease-in-out infinite;

  &:nth-child(2) {
    animation-delay: 0.2s;
  }
  &:nth-child(3) {
    animation-delay: 0.4s;
  }
}

.-headline {
  margin: 0;
  overflow: hidden;
  font-family: 'Montserrat', sans-serif;
  font-weight: 800;
  font-size: 38px;
  line-height: 1.1;
  color: #fff;
  text-wrap: balance;
  max-width: 720px;
  animation: fpgRevealH 0.7s $ease-out-expo 0.5s both;

  span {
    color: #00abdf;
  }
}

.-subtext {
  margin: 0;
  overflow: hidden;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  line-height: 1.55;
  color: #aeb3d8;
  max-width: 520px;
  animation: fpgRevealP 0.65s $ease-out-expo 0.95s both;
}
.-nowrap {
  white-space: nowrap;
}

.-cta {
  overflow: hidden;
  animation: fpgRevealCta 0.65s $ease-out-expo 1.35s both;
}
.-cta-row {
  display: flex;
  align-items: center;
  gap: 18px;
  padding-top: 2px;
}
.-qr-card {
  background: #fff;
  border-radius: 12px;
  padding: 4px;
  flex-shrink: 0;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
  animation: fpgPopIn 0.6s $ease-back 1.75s both;
}
.-qr {
  display: block;
  width: 120px;
  height: 120px;
}
.-cta-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.-scan {
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 16px;
  color: #fff;
}
.-stores {
  display: flex;
  align-items: center;
  gap: 16px;
}
.-store {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #aeb3d8;
  text-decoration: none;
  transition: color 0.15s ease;

  svg {
    width: 15px;
    height: 15px;
    fill: currentColor;
  }

  &:hover {
    color: #fff;
  }
}
</style>
